/**
 * State shape for a single AI Insights conversation. One instance exists per mounted chat
 * panel — the trigger on a calculation, a report, or a variance row each own an independent
 * conversation, so this is deliberately not shared through a context provider.
 */

export interface Message {
    role: 'user' | 'assistant';
    text: string;
}

/**
 * Marks an assistant entry as a transient indexing notice rather than agent content, so
 * FAIL_REQUEST can discard it. Lives here rather than in the component layer because the
 * reducer both writes and tests for it, and the service layer must not depend on components.
 */
export const INDEXING_PREFIX = '⏳ ';

/** The CLI / report the conversation is scoped to, sent to the agent as context. */
export interface AIInsightsContextValues {
    cliId: string;
    reportType?: string;
    accountingPeriod?: string;
    accountingDay?: string;
}

export interface AIInsightsState {
    /** Ordered transcript. The trailing assistant entry is the streaming target. */
    messages: Message[];
    /** True while a request is in flight, from send until the socket settles. */
    loading: boolean;
    /** User-facing failure text; empty when there is nothing to report. */
    error: string;
    /** Report the agent triggered and we are waiting on, if any. */
    pollingReportId?: string;
    /** Epoch ms the current poll started, used to derive elapsed time and the timeout. */
    pollingStartedAt?: number;
    /** Milliseconds spent polling, surfaced in the UI. */
    pollingElapsed: number;
}

export const initialAIInsightsState: AIInsightsState = {
    messages: [],
    loading: false,
    error: '',
    pollingReportId: undefined,
    pollingStartedAt: undefined,
    pollingElapsed: 0,
};
